/**
 * MEVIA V2 - Pairwise Comparison Assessment Component
 * 
 * This component implements the pairwise comparison evaluation method.
 * Users compare 45 pairs of statements and choose which one represents them better.
 * Uses the Bradley-Terry model for scoring.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Globe, HelpCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

// Dimension labels for display
const DIMENSION_LABELS = {
  es: {
    physical: 'Físico',
    human: 'Humano',
    manufactured: 'Herramientas',
    financial: 'Financiero',
    natural: 'Natural',
    cultural: 'Cultural',
    social: 'Social',
    knowledge: 'Conocimiento',
    psychological: 'Psicológico',
    purpose: 'Propósito'
  },
  en: {
    physical: 'Physical',
    human: 'Human',
    manufactured: 'Tools',
    financial: 'Financial',
    natural: 'Natural',
    cultural: 'Cultural',
    social: 'Social',
    knowledge: 'Knowledge',
    psychological: 'Psychological',
    purpose: 'Purpose'
  }
};

const TEXTS = {
  es: {
    title: 'Evaluación por Comparación',
    subtitle: 'Elige la declaración que mejor te represente',
    offerPhase: 'Fase 1: Lo que ofreces',
    needPhase: 'Fase 2: Lo que necesitas',
    offerDescription: 'Compara las declaraciones y selecciona la que mejor describe lo que TÚ OFRECES en tu trabajo.',
    needDescription: 'Compara las declaraciones y selecciona la que mejor describe lo que TÚ NECESITAS de tu trabajo.',
    progress: 'Progreso',
    pairOf: 'de',
    selectOption: 'Selecciona una opción',
    optionA: 'Opción A',
    optionB: 'Opción B',
    next: 'Siguiente',
    previous: 'Anterior',
    complete: 'Completar Fase',
    completeAssessment: 'Completar Evaluación',
    loading: 'Cargando...',
    saving: 'Guardando...',
    saved: 'Guardado',
    timeRemaining: 'Tiempo estimado restante',
    minutes: 'minutos',
    tip: 'Consejo',
    tipText: 'No pienses demasiado. Tu primera reacción suele ser la más auténtica.',
    phaseComplete: 'Fase Completada',
    continueToNeeds: 'Continuar a "Lo que necesitas"',
    viewResults: 'Ver Resultados',
    comparing: 'Comparando'
  },
  en: {
    title: 'Comparison Assessment',
    subtitle: 'Choose the statement that best represents you',
    offerPhase: 'Phase 1: What you offer',
    needPhase: 'Phase 2: What you need',
    offerDescription: 'Compare the statements and select the one that best describes what YOU OFFER at work.',
    needDescription: 'Compare the statements and select the one that best describes what YOU NEED from work.',
    progress: 'Progress',
    pairOf: 'of',
    selectOption: 'Select an option',
    optionA: 'Option A',
    optionB: 'Option B',
    next: 'Next',
    previous: 'Previous',
    complete: 'Complete Phase',
    completeAssessment: 'Complete Assessment',
    loading: 'Loading...',
    saving: 'Saving...',
    saved: 'Saved',
    timeRemaining: 'Estimated time remaining',
    minutes: 'minutes',
    tip: 'Tip',
    tipText: "Don't overthink it. Your first reaction is usually the most authentic.",
    phaseComplete: 'Phase Complete',
    continueToNeeds: 'Continue to "What you need"',
    viewResults: 'View Results',
    comparing: 'Comparing'
  }
};

const PairwiseAssessment = ({ assessmentId, onComplete }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  const dimLabels = DIMENSION_LABELS[language] || DIMENSION_LABELS.es;

  // State
  const [phase, setPhase] = useState('offer'); // 'offer' or 'need'
  const [evaluationId, setEvaluationId] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [phaseComplete, setPhaseComplete] = useState(false);
  const [offerComplete, setOfferComplete] = useState(false);
  const [needComplete, setNeedComplete] = useState(false);

  // Load pairs on mount
  useEffect(() => {
    startEvaluation();
  }, [assessmentId, phase]);

  const startEvaluation = async () => {
    setLoading(true);
    try {
      // Start pairwise evaluation for current phase
      const startResponse = await axios.post(
        `/v2/pairwise/start?assessment_id=${assessmentId}&evaluation_type=${phase}`
      );

      if (startResponse.data.success) {
        const evalData = startResponse.data.data;
        setEvaluationId(evalData.id);

        // Get pairs with statements
        const pairsResponse = await axios.get(
          `/v2/pairwise/${evalData.id}/pairs?language=${language}`
        );

        if (pairsResponse.data.success) {
          const pairsData = pairsResponse.data.data;
          setPairs(pairsData.pairs);
          
          // Restore previous responses if any
          const previousResponses = {};
          pairsData.pairs.forEach(pair => {
            if (pair.responded && pair.winner) {
              previousResponses[pair.pair_index] = pair.winner;
            }
          });
          setResponses(previousResponses);
          
          // Find first unanswered pair
          const firstUnanswered = pairsData.pairs.findIndex(p => !p.responded);
          setCurrentPairIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
          
          // Check if phase is already complete
          if (Object.keys(previousResponses).length === 45) {
            setPhaseComplete(true);
            if (phase === 'offer') setOfferComplete(true);
            if (phase === 'need') setNeedComplete(true);
          }
        }
      }
    } catch (error) {
      logger.error('Error starting evaluation:', error);
      toast.error('Error al iniciar la evaluación');
    } finally {
      setLoading(false);
      setStartTime(Date.now());
    }
  };

  const handleSelectOption = async (winner) => {
    if (saving) return;
    
    setSelectedOption(winner);
    setSaving(true);

    const currentPair = pairs[currentPairIndex];
    const responseTime = startTime ? Date.now() - startTime : null;

    try {
      await axios.post(
        `/v2/pairwise/${evaluationId}/respond?pair_index=${currentPair.pair_index}&winner=${winner}&response_time_ms=${responseTime}`
      );

      // Update local state
      setResponses(prev => ({
        ...prev,
        [currentPair.pair_index]: winner
      }));

      // Auto advance after short delay
      setTimeout(() => {
        if (currentPairIndex < pairs.length - 1) {
          setCurrentPairIndex(prev => prev + 1);
          setSelectedOption(null);
          setStartTime(Date.now());
        } else {
          // Phase complete
          setPhaseComplete(true);
          if (phase === 'offer') setOfferComplete(true);
          if (phase === 'need') setNeedComplete(true);
        }
        setSaving(false);
      }, 300);

    } catch (error) {
      logger.error('Error saving response:', error);
      toast.error('Error al guardar respuesta');
      setSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentPairIndex > 0) {
      setCurrentPairIndex(prev => prev - 1);
      const prevPair = pairs[currentPairIndex - 1];
      setSelectedOption(responses[prevPair.pair_index] || null);
    }
  };

  const handleNext = () => {
    if (currentPairIndex < pairs.length - 1) {
      setCurrentPairIndex(prev => prev + 1);
      const nextPair = pairs[currentPairIndex + 1];
      setSelectedOption(responses[nextPair.pair_index] || null);
    }
  };

  const handleCompletePhase = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`/v2/pairwise/${evaluationId}/complete`);
      
      if (response.data.success) {
        toast.success(texts.phaseComplete);
        
        if (phase === 'offer') {
          setOfferComplete(true);
          // Move to need phase
          setPhase('need');
          setPhaseComplete(false);
          setResponses({});
          setCurrentPairIndex(0);
        } else {
          setNeedComplete(true);
          // Both phases complete - calculate HR indicators
          await calculateIndicators();
          if (onComplete) {
            onComplete();
          } else {
            navigate('/dashboard');
          }
        }
      }
    } catch (error) {
      logger.error('Error completing phase:', error);
      toast.error('Error al completar la fase');
    } finally {
      setSaving(false);
    }
  };

  const calculateIndicators = async () => {
    try {
      await axios.post(`/v2/hr-indicators/calculate/${assessmentId}`);
      toast.success('Indicadores calculados correctamente');
    } catch (error) {
      logger.error('Error calculating indicators:', error);
    }
  };

  // Calculate progress
  const answeredCount = Object.keys(responses).length;
  const progressPercent = (answeredCount / 45) * 100;
  const estimatedMinutes = Math.ceil((45 - answeredCount) * 0.4);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{texts.loading}</p>
        </div>
      </div>
    );
  }

  const currentPair = pairs[currentPairIndex];

  // Phase complete screen
  if (phaseComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-gray-900">
                {texts.phaseComplete}
              </CardTitle>
              <CardDescription className="text-lg">
                {phase === 'offer' ? texts.offerPhase : texts.needPhase}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-6 pt-4">
              {phase === 'offer' ? (
                <Button 
                  onClick={handleCompletePhase}
                  className="w-full sm:w-auto px-8 py-3 bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                >
                  {saving ? texts.saving : texts.continueToNeeds}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              ) : (
                <Button 
                  onClick={handleCompletePhase}
                  className="w-full sm:w-auto px-8 py-3 bg-green-600 hover:bg-green-700"
                  disabled={saving}
                >
                  {saving ? texts.saving : texts.completeAssessment}
                  <CheckCircle className="ml-2 w-5 h-5" />
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-gray-900">
                  {texts.title}
                </CardTitle>
                <CardDescription>
                  {phase === 'offer' ? texts.offerPhase : texts.needPhase}
                </CardDescription>
              </div>
              <Badge variant="outline" className="px-3 py-1">
                <Clock className="w-4 h-4 mr-1" />
                ~{estimatedMinutes} {texts.minutes}
              </Badge>
            </div>
            
            {/* Progress bar */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{texts.progress}</span>
                <span>{answeredCount} {texts.pairOf} 45</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
          </CardHeader>
        </Card>

        {/* Tip card */}
        <Card className="bg-blue-50 border-blue-200 shadow-sm">
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900">{texts.tip}</p>
                <p className="text-sm text-blue-700">{texts.tipText}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main comparison card */}
        {currentPair && (
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-2 text-center">
              <CardDescription className="text-base">
                {texts.comparing}: <span className="font-medium">{dimLabels[currentPair.dimension_a]}</span> vs <span className="font-medium">{dimLabels[currentPair.dimension_b]}</span>
              </CardDescription>
              <p className="text-sm text-gray-500">
                {phase === 'offer' ? texts.offerDescription : texts.needDescription}
              </p>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {/* Option A */}
              <button
                onClick={() => handleSelectOption('A')}
                disabled={saving}
                className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                  selectedOption === 'A' || responses[currentPair.pair_index] === 'A'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg ${
                    selectedOption === 'A' || responses[currentPair.pair_index] === 'A'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    A
                  </div>
                  <div className="flex-1">
                    <p className="text-base text-gray-900 leading-relaxed">
                      {currentPair.statement_a?.text || currentPair.dimension_a}
                    </p>
                    <Badge variant="secondary" className="mt-2 text-xs">
                      {dimLabels[currentPair.dimension_a]}
                    </Badge>
                  </div>
                </div>
              </button>

              {/* VS divider */}
              <div className="flex items-center justify-center">
                <div className="h-px bg-gray-200 flex-1"></div>
                <span className="px-4 text-gray-400 font-medium">VS</span>
                <div className="h-px bg-gray-200 flex-1"></div>
              </div>

              {/* Option B */}
              <button
                onClick={() => handleSelectOption('B')}
                disabled={saving}
                className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                  selectedOption === 'B' || responses[currentPair.pair_index] === 'B'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg ${
                    selectedOption === 'B' || responses[currentPair.pair_index] === 'B'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    B
                  </div>
                  <div className="flex-1">
                    <p className="text-base text-gray-900 leading-relaxed">
                      {currentPair.statement_b?.text || currentPair.dimension_b}
                    </p>
                    <Badge variant="secondary" className="mt-2 text-xs">
                      {dimLabels[currentPair.dimension_b]}
                    </Badge>
                  </div>
                </div>
              </button>

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentPairIndex === 0 || saving}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {texts.previous}
                </Button>

                <div className="text-center">
                  <span className="text-sm text-gray-500">
                    {currentPairIndex + 1} / 45
                  </span>
                </div>

                {answeredCount === 45 ? (
                  <Button
                    onClick={handleCompletePhase}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {saving ? texts.saving : texts.complete}
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleNext}
                    disabled={currentPairIndex === pairs.length - 1 || saving}
                  >
                    {texts.next}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress indicators */}
        <Card className="shadow-sm border-0">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-1 justify-center">
              {pairs.map((pair, idx) => (
                <button
                  key={pair.pair_index}
                  onClick={() => {
                    setCurrentPairIndex(idx);
                    setSelectedOption(responses[pair.pair_index] || null);
                  }}
                  className={`w-6 h-6 rounded-full text-xs font-medium transition-all ${
                    idx === currentPairIndex
                      ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                      : responses[pair.pair_index]
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PairwiseAssessment;
